/**
 * FR-E28: the month's GSTR-2A against its GSTR-2B, in sentences.
 *
 * The service compares (`recon/gstr2a.py::compare_2a_2b`) and writes each
 * row's reason (`because`); this turns the view into the lines the
 * Reconciliation screen's *2A vs 2B* tab prints. Nothing here decides
 * anything about credit: a 2A never does (V-h), and the sentences say so.
 */

export const HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const

export interface TwoAVsTwoBRow {
  key: string
  supplier_gstin: string
  supplier_name: string | null
  doc_no: string
  doc_date: string | null
  doc_kind: string
  taxable: number
  igst: number
  cgst: number
  sgst: number
  cess: number
  because: string
  /** `only_in_2a`: the later 2B that carries the row, when one is imported. */
  in_2b_period?: string | null
  filing_status?: string | null
  filing_period?: string | null
  /** `differences`: per head, the two figures and 2B less 2A — paise. */
  heads?: Record<string, { '2a': number; '2b': number; difference: number }>
}

export interface StatementAbout {
  id: string
  version_no: number
  row_count: number
}

export interface TwoAVsTwoB {
  state: 'no_2a' | 'no_2b' | 'compared'
  period: string
  gstr2a?: StatementAbout | null
  gstr2b?: StatementAbout | null
  only_in_2a?: TwoAVsTwoBRow[]
  only_in_2b?: TwoAVsTwoBRow[]
  differences?: TwoAVsTwoBRow[]
  agreed?: number
  totals?: Record<string, Record<string, number>>
}

/** Indian digit grouping, two decimals, from paise. */
export function rupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** The tax a row carries across the four heads — paise, a magnitude. */
export function taxOf(row: Pick<TwoAVsTwoBRow, 'igst' | 'cgst' | 'sgst' | 'cess'>): number {
  return Math.abs(HEADS.reduce((sum, head) => sum + (row[head] ?? 0), 0))
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** How many rows the tab has something to say about — its badge. */
export function countOf(view: TwoAVsTwoB | null): number {
  if (!view || view.state !== 'compared') return 0
  return (
    (view.only_in_2a?.length ?? 0) +
    (view.only_in_2b?.length ?? 0) +
    (view.differences?.length ?? 0)
  )
}

/**
 * The one line at the top of the tab. For a month one statement is missing,
 * it says which and what to do; otherwise the four counts.
 */
export function headline(view: TwoAVsTwoB | null, period: string): string {
  if (!view) return `Pick a period (MM-YYYY) to compare its GSTR-2A with its GSTR-2B.`
  if (view.state === 'no_2a') {
    return (
      `No GSTR-2A is imported for ${period}. Import the portal’s 2A (.xlsx or .json) with ` +
      `Import 2A — it is stored beside the 2B and claims nothing.`
    )
  }
  if (view.state === 'no_2b') {
    return (
      `A GSTR-2A is imported for ${period} (version ${view.gstr2a?.version_no ?? 1}, ` +
      `${plural(view.gstr2a?.row_count ?? 0, 'row', 'rows')}) but no GSTR-2B is. ` +
      `Import the 2B first; credit is claimed only against it.`
    )
  }
  const a = view.gstr2a
  const b = view.gstr2b
  return (
    `GSTR-2A version ${a?.version_no ?? 1} (${plural(a?.row_count ?? 0, 'row', 'rows')}) ` +
    `against GSTR-2B version ${b?.version_no ?? 1} (${plural(b?.row_count ?? 0, 'row', 'rows')}) ` +
    `for ${period}: ${plural(view.agreed ?? 0, 'row agrees', 'rows agree')}, ` +
    `${plural(view.only_in_2a?.length ?? 0, 'is', 'are')} in the 2A only, ` +
    `${plural(view.only_in_2b?.length ?? 0, 'is', 'are')} in the 2B only, ` +
    `${plural(view.differences?.length ?? 0, 'differs', 'differ')} in amount. ` +
    `Credit is claimed against the 2B alone; the 2A is what the suppliers have filed since.`
  )
}

/**
 * The statement's `doc_kind` vocabulary (`recon/excel_2b.py::_DOCUMENT_SHEETS`)
 * in words. A kind not listed reads as "document" rather than as its code.
 */
export const DOC_KIND_WORDS: Record<string, string> = {
  invoice: 'invoice',
  invoice_amendment: 'amended invoice',
  credit_note: 'credit note',
  credit_note_amendment: 'amended credit note',
  debit_note: 'debit note',
  debit_note_amendment: 'amended debit note',
  eco: 'e-commerce invoice',
  eco_amendment: 'amended e-commerce invoice',
  isd: 'ISD document',
  isd_amendment: 'amended ISD document',
  import: 'bill of entry',
  import_sez: 'bill of entry from an SEZ',
}

/** What a row is: `INV-001 of 05-07-2026 from Steel Co (27AAACS1111A1Z1), ₹1,800.00 of tax`. */
export function rowWords(row: TwoAVsTwoBRow): string {
  const who = row.supplier_name
    ? `${row.supplier_name} (${row.supplier_gstin || 'no GSTIN'})`
    : row.supplier_gstin || 'a supplier with no GSTIN'
  const what = DOC_KIND_WORDS[row.doc_kind] ?? 'document'
  const when = row.doc_date ? ` of ${row.doc_date}` : ''
  return `${what} ${row.doc_no}${when} from ${who}, ₹${rupees(taxOf(row))} of tax`
}

/** The full sentence a list prints for one row: what it is, then why it is listed. */
export function rowSentence(row: TwoAVsTwoBRow): string {
  const words = rowWords(row)
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} — ${row.because}`
}

/** For a `differences` row, each head as `IGST ₹180.00 on the 2A, ₹170.00 on the 2B`. */
export function headWords(row: TwoAVsTwoBRow): string[] {
  const heads = row.heads ?? {}
  return Object.entries(heads).map(
    ([head, figures]) =>
      `${head.toUpperCase()} ₹${rupees(figures['2a'])} on the 2A, ₹${rupees(figures['2b'])} on the 2B`,
  )
}

/** The three section headings, with their counts. */
export function sectionTitles(view: TwoAVsTwoB): { key: 'only_in_2a' | 'only_in_2b' | 'differences'; title: string }[] {
  return [
    {
      key: 'only_in_2a',
      title: `In the 2A, not in this month's 2B (${view.only_in_2a?.length ?? 0})`,
    },
    {
      key: 'only_in_2b',
      title: `In the 2B, not in the 2A (${view.only_in_2b?.length ?? 0})`,
    },
    {
      key: 'differences',
      title: `In both, with different figures (${view.differences?.length ?? 0})`,
    },
  ]
}
